/*
 * POS finance backend client (day opening + cash-out expenses).
 *
 * Thin POS-side wrappers over the dedicated POS finance endpoints:
 *   POST   /api/day-opening        — persist the start-of-day float
 *   POST   /api/pos-expenses       — persist a single cash-out expense
 *   DELETE /api/pos-expenses/{id}  — soft-delete a cash-out expense
 *
 * Offline-first: AsyncStorage stays the source of truth on the terminal; these
 * calls are best-effort and the callers swallow failures so a network blip
 * never blocks the cashier. Mirrors the plain `fetch` + Bearer pattern of
 * [[dailyClosingApi.js]] (POS mode does not use the swaggerClient).
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { getAccessToken, getUserInfo, getOrCreateDeviceTrustId } from './storage';
import { getDeviceInfo, getDeviceHeaders } from './deviceInfo';

const API_URL = 'https://api.happysolutions.hu/api';

/** AsyncStorage key holding the current backend day-opening id (link target). */
export const DAY_OPENING_ID_KEY = 'dayOpening_id';

/** Safely parse a JSON AsyncStorage value, returning `fallback` on any error. */
const readJson = async (key, fallback = null) => {
    try {
        const raw = await AsyncStorage.getItem(key);

        return raw ? JSON.parse(raw) : fallback;
    } catch (_e) {
        return fallback;
    }
};

/**
 * Collect the common scope (restaurant, place, operator, device) every POS
 * finance request needs, read from the same AsyncStorage keys the day-close
 * flow uses.
 *
 * @returns {Promise<object>} `{ restaurantId, restaurantName, restaurantPlaceId, restaurantPlaceName, user, deviceId }`
 */
export async function getPosScope() {
    const [user, restaurant, restaurantPlace, deviceId] = await Promise.all([
        getUserInfo(),
        readJson('restaurant', {}),
        readJson('restaurantPlace', {}),
        getOrCreateDeviceTrustId(),
    ]);

    return {
        restaurantId: restaurant?._id,
        restaurantName: restaurant?.name || '',
        restaurantPlaceId: restaurantPlace?._id,
        restaurantPlaceName: restaurantPlace?.name || restaurantPlace?.raw || '',
        user: {
            id: user?._id,
            firstName: user?.name?.first || user?.firstName || '',
            lastName: user?.name?.last || user?.lastName || '',
            email: user?.email || '',
        },
        deviceId: deviceId || '',
    };
}

/** Shared authenticated JSON fetch; throws with `.status`/`.body` on non-2xx. */
async function request(path, { method = 'POST', body } = {}) {
    const [token, deviceHeaders] = await Promise.all([
        getAccessToken(),
        getDeviceHeaders().catch(() => ({})),
    ]);

    // A missing token would send `Authorization: Bearer null` → backend 401.
    // Fail loudly so callers don't silently drop the day-open/expense sync.
    if (!token) {
        const err = new Error('Nincs érvényes hozzáférési token (bejelentkezés szükséges).');

        err.status = 401;
        throw err;
    }

    const res = await fetch(`${API_URL}${path}`, {
        method,
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            ...deviceHeaders,
        },
        body: body ? JSON.stringify(body) : undefined,
    });

    const text = await res.text();
    let json = {};

    try {
        json = text ? JSON.parse(text) : {};
    } catch (_e) {
        json = {};
    }

    if (!res.ok) {
        const err = new Error(json?.message || `HTTP ${res.status}`);

        err.status = res.status;
        err.body = json;
        throw err;
    }

    return json;
}

/**
 * Persist the start-of-day opening float and remember the returned id so
 * later expenses/closing can link to this session.
 *
 * @param {object} params
 * @param {object} params.amounts `{ cash, currency, creditCard, voucher, other }`.
 * @param {number} [params.openingCash] Headline opening cash (defaults to amounts.cash).
 * @returns {Promise<object>} Parsed response (`{ opening: { _id, ... } }`).
 */
export async function sendDayOpening({ amounts = {}, openingCash } = {}) {
    const [scope, deviceInfo] = await Promise.all([getPosScope(), getDeviceInfo()]);
    const json = await request('/day-opening', {
        body: {
            ...scope,
            deviceInfo,
            amounts,
            openingCash,
            openedAt: new Date().toISOString(),
        },
    });

    if (json?.opening?._id) {
        try {
            await AsyncStorage.setItem(DAY_OPENING_ID_KEY, String(json.opening._id));
        } catch (_e) {
            // Non-fatal — linking is best-effort.
        }
    }

    return json;
}

/**
 * Persist a single cash-out expense, linked to the current day opening.
 *
 * @param {object} expense `{ costType, costTypeLabel, note, description, price|amount, createdAt }`.
 * @returns {Promise<object>} Parsed response (`{ expense: { _id, ... } }`).
 */
export async function sendPosExpense(expense = {}) {
    const [scope, dayOpeningId, deviceInfo] = await Promise.all([
        getPosScope(),
        AsyncStorage.getItem(DAY_OPENING_ID_KEY),
        getDeviceInfo(),
    ]);

    return request('/pos-expenses', {
        body: {
            ...scope,
            deviceInfo,
            dayOpeningId: dayOpeningId || undefined,
            costType: expense.costType,
            costTypeLabel: expense.costTypeLabel,
            note: expense.note,
            description: expense.description,
            amount: Number(expense.amount != null ? expense.amount : expense.price) || 0,
            date: expense.createdAt || new Date().toISOString(),
        },
    });
}

/**
 * Soft-delete a previously-synced cash-out expense.
 *
 * @param {string} serverId The backend PosExpense id.
 * @returns {Promise<object>} Parsed response.
 */
export async function deletePosExpense(serverId) {
    return request(`/pos-expenses/${serverId}`, { method: 'DELETE' });
}

export default {
    getPosScope,
    sendDayOpening,
    sendPosExpense,
    deletePosExpense,
    DAY_OPENING_ID_KEY,
};
